import React, { useState, useEffect, useCallback } from 'react';
import { FiMail, FiCheckCircle, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';

const cfg = () => {
  const token = localStorage.getItem('authToken');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

// Profile panel: connect your own company mailbox so client email lands on the right job.
// The password is verified before storing and is kept encrypted server-side.
export default function ConnectMyEmail() {
  const [st, setSt] = useState(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [sig, setSig] = useState(null);
  const [editingSig, setEditingSig] = useState(false);
  const [sigText, setSigText] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/my-email/status`, cfg());
      setSt(res.data);
    } catch (e) { /* panel hides */ }
  }, []);

  const loadSig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/my-signature`, cfg());
      setSig(res.data);
      setSigText(res.data?.custom || '');
    } catch (e) { /* signature block hides */ }
  }, []);

  const saveSig = async (clear) => {
    setBusy(true); setErr(''); setNote('');
    try {
      const res = await axios.put(`${API_URL}/my-signature`, { signature: clear ? '' : sigText }, cfg());
      setSig(s => ({ ...s, ...res.data, custom: clear ? '' : sigText }));
      if (clear) setSigText('');
      setEditingSig(false);
      setNote(clear ? 'Back to the automatic signature.' : 'Signature saved.');
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not save signature.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSig(); }, [loadSig]);

  if (!st || !st.eligible) return null;  // no company address → nothing to connect

  const run = async (path, body, okMsg) => {
    setBusy(true); setNote(''); setErr('');
    try {
      const res = await axios.post(`${API_URL}/my-email/${path}`, body || {}, cfg());
      setNote(res.data?.message || okMsg);
      setPassword('');
      await load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const btn = (bg, color) => ({
    padding: '9px 14px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 13,
    background: bg, color, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
  });

  return (
    <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 20, marginBottom: 15, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#666', display: 'flex', alignItems: 'center', gap: 8 }}>
        <FiMail size={16} /> My Email
      </h3>
      <p style={{ margin: '0 0 14px', fontSize: 12.5, color: '#9ca3af', lineHeight: 1.5 }}>
        Connect <strong>{st.email}</strong> so client emails show up on the right job automatically.
        Your password is checked first, then stored encrypted — only mail that matches a client or
        job number is pulled in, and anything unmatched is visible only to you.
      </p>

      {!st.server_ready && (
        <div style={{ background: '#fef3c7', color: '#92400e', borderRadius: 8, padding: 10, fontSize: 13, marginBottom: 12 }}>
          The server isn't set up for mailbox connections yet.
        </div>
      )}

      {st.connected ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#166534', background: '#dcfce7', borderRadius: 8, padding: '10px 12px', fontSize: 13.5, marginBottom: 10 }}>
            <FiCheckCircle size={15} /> Connected
            {st.last_synced_at && (
              <span style={{ color: '#3f6212', marginLeft: 'auto', fontSize: 12 }}>
                last checked {new Date(st.last_synced_at + (st.last_synced_at.endsWith('Z') ? '' : 'Z')).toLocaleString()}
              </span>
            )}
          </div>
          {st.last_result && st.last_result.startsWith('error') && (
            <div style={{ display: 'flex', gap: 8, color: '#991b1b', background: '#fee2e2', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, marginBottom: 10 }}>
              <FiAlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>Last sync failed: {st.last_result.replace(/^error:\s*/, '')} — if you changed your password, reconnect below.</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={btn('#f1f5f9', '#334155')} disabled={busy} onClick={() => run('test', {}, 'Connection OK')}>
              <FiRefreshCw size={12} style={{ marginRight: 5 }} /> Test connection
            </button>
            <button style={btn('#fee2e2', '#991b1b')} disabled={busy} onClick={() => {
              if (window.confirm('Disconnect your mailbox? Email already on jobs stays put.')) run('disconnect', {}, 'Disconnected');
            }}>
              Disconnect
            </button>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={`Password for ${st.email}`}
            autoComplete="new-password"
            style={{ flex: 1, minWidth: 200, padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
          />
          <button
            style={btn('#14532d', 'white')}
            disabled={busy || !password.trim() || !st.server_ready}
            onClick={() => run('connect', { password }, 'Connected')}
          >
            {busy ? 'Checking…' : 'Connect'}
          </button>
        </div>
      )}

      {/* Email signature — auto-built from your digital card, editable if you want */}
      {sig && (
        <div style={{ marginTop: 16, borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <strong style={{ fontSize: 13.5, color: '#334155' }}>Email signature</strong>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              {sig.is_custom ? 'custom' : 'automatic — from your digital card'}
            </span>
            {!editingSig && (
              <button onClick={() => setEditingSig(true)}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#2563eb', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                Customize
              </button>
            )}
          </div>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9ca3af' }}>
            Added to job emails you write (not automated alerts, and not quotes/invoices — those send from the company address).
          </p>

          {editingSig ? (
            <>
              <textarea
                value={sigText}
                onChange={(e) => setSigText(e.target.value)}
                rows={5}
                placeholder="Leave blank to use the automatic signature"
                style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button style={btn('#14532d', 'white')} disabled={busy} onClick={() => saveSig(false)}>Save</button>
                <button style={btn('#f1f5f9', '#334155')} disabled={busy} onClick={() => { setEditingSig(false); setSigText(sig.custom || ''); }}>Cancel</button>
                {sig.is_custom && (
                  <button style={btn('#fee2e2', '#991b1b')} disabled={busy} onClick={() => saveSig(true)}>Use automatic instead</button>
                )}
              </div>
            </>
          ) : (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, fontSize: 12.5, color: '#475569', whiteSpace: 'pre-wrap' }}>
              {(sig.preview_text || '').replace(/^\n+/, '').replace(/^--\n/, '') || '(nothing to show)'}
            </div>
          )}
        </div>
      )}

      {note && <div style={{ marginTop: 10, fontSize: 13, color: '#166534' }}>{note}</div>}
      {err && <div style={{ marginTop: 10, fontSize: 13, color: '#b91c1c' }}>{err}</div>}
    </div>
  );
}
